import { useState, useEffect } from 'react';
import { useUser } from '../../contexts/AuthContext';
import { customersService } from '../../services/api';
import { US_STATES } from '../../utils/usStates';
import {
  formatCityName,
  formatZipCode,
  formatStreetAddress
} from '../../utils/addressFormatters';

function CustomerCreateModal({
  isOpen,
  onClose,
  onCustomerCreated,
  initialData = {}
}) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState({
    company: initialData?.company || '',
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    zipCode: initialData?.zipCode || '',
    notes: initialData?.notes || ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Check for changes in form data
  useEffect(() => {
    const hasChanges = Object.keys(formData).some(key => {
      const currentValue = formData[key];
      const initialValue = initialData[key] || '';
      return currentValue !== initialValue && currentValue !== '';
    });
    setHasUnsavedChanges(hasChanges);
  }, [formData, initialData]);

  // Handle form input changes with real-time formatting where appropriate
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case 'address':
        formattedValue = value.slice(0, 100);
        break;
      case 'city':
        formattedValue = value.slice(0, 50);
        break;
      case 'zipCode':
        formattedValue = formatZipCode(value);
        break;
      default:
        formattedValue = value;
    }

    setFormData({
      ...formData,
      [name]: formattedValue
    });

    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Apply final formatting on blur (avoids interfering while user is typing)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    switch (name) {
      case 'address':
        finalValue = formatStreetAddress(value);
        break;
      case 'city':
        finalValue = formatCityName(value);
        break;
      case 'zipCode':
        finalValue = formatZipCode(value);
        break;
      default:
        break;
    }

    if (finalValue !== value) {
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Require either company name or first name + last name
    if (!formData.company && (!formData.firstName || !formData.lastName)) {
      if (!formData.company) {
        newErrors.company = "Company name is required if individual name is not provided";
      }
      if (!formData.firstName) {
        newErrors.firstName = "First name is required if company name is not provided";
      }
      if (!formData.lastName) {
        newErrors.lastName = "Last name is required if company name is not provided";
      }
      isValid = false;
    }

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
      isValid = false;
    }

    // Phone validation (simple check)
    if (formData.phone && !/^[0-9+\-() ]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = "Phone number is invalid";
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await customersService.createCustomer({
        ...formData,
        userId: user.id
      });
      
      // Call the callback with the created customer
      if (onCustomerCreated) {
        onCustomerCreated(response);
      }
      
      // Close the modal
      onClose();
    } catch (err) {
      console.error('Failed to create customer:', err);
      // You might want to show an error message here
      alert('Failed to create customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

// Handle modal close with unsaved changes
const handleCloseModal = () => {
  if (hasUnsavedChanges) {
    setShowConfirmDialog(true);
  } else {
    // Reset form when closing
    setFormData({
      company: initialData?.company || '',
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      address: initialData?.address || '',
      city: initialData?.city || '',
      state: initialData?.state || '',
      zipCode: initialData?.zipCode || '',
      notes: initialData?.notes || ''
    });
    setFormErrors({});
    setHasUnsavedChanges(false);
    onClose();
  }
};

  // Handle confirmation dialog actions
  const handleConfirmClose = (e) => {
    e.stopPropagation();
    setShowConfirmDialog(false);
    onClose();
  };

  const handleCancelClose = (e) => {
    e.stopPropagation();
    setShowConfirmDialog(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={handleCloseModal}
      >
        <div 
          className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Create New Customer</h2>
            <button 
              onClick={handleCloseModal}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Company Name</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg ${formErrors.company ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Company Name"
              />
              {formErrors.company && (
                <p className="text-red-500 text-sm mt-1">{formErrors.company}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="First Name"
                />
                {formErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Last Name"
                />
                {formErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lastName}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Email"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Phone"
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Street Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">State</label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select</option>
                  {US_STATES.map(state => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">ZIP Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength="10"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="12345"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg"
                rows="3"
                placeholder="Additional Notes"
              ></textarea>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover disabled:opacity-50 font-semibold transition-colors"
              >
                {loading ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCancelClose}
        >
          <div 
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
            <p className="text-gray-600 mb-4">
              You have unsaved changes. Are you sure you want to close?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleCancelClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmClose}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Close Without Saving
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CustomerCreateModal;